<template>
  <div class="labourer-readiness-dashboard">
    <md-card>
      <md-card-header class="md-card-header-icon md-card-header-primary">
        <div class="card-icon">
          <md-icon>analytics</md-icon>
        </div>
        <h4 class="title">Labourer Readiness Dashboard</h4>
        <p class="category">Monitor labourer completeness for current workplace assignments</p>
      </md-card-header>

      <md-card-content>
        <!-- Statistics Cards -->
        <div class="stats-grid">
          <div class="stat-card" :class="{ active: filterStatus === 'all' }" @click="setFilter('all')">
            <div class="stat-icon">
              <md-icon>people</md-icon>
            </div>
            <div class="stat-content">
              <div class="stat-value">{{ formatNumber(stats.total) }}</div>
              <div class="stat-label">Total Labourers</div>
            </div>
          </div>

          <div class="stat-card success" :class="{ active: filterStatus === 'complete' }"
               @click="setFilter('complete')">
            <div class="stat-icon">
              <md-icon>check_circle</md-icon>
            </div>
            <div class="stat-content">
              <div class="stat-value">{{ formatNumber(stats.complete) }}</div>
              <div class="stat-label">Complete</div>
              <div class="stat-percentage">{{ stats.completion_percentage }}%</div>
            </div>
          </div>

          <div class="stat-card warning" :class="{ active: filterStatus === 'incomplete' }"
               @click="setFilter('incomplete')">
            <div class="stat-icon">
              <md-icon>warning</md-icon>
            </div>
            <div class="stat-content">
              <div class="stat-value">{{ formatNumber(stats.incomplete) }}</div>
              <div class="stat-label">Incomplete</div>
            </div>
          </div>

          <div class="stat-card info" :class="{ active: filterStatus === 'skill_mismatch' }"
               @click="setFilter('skill_mismatch')">
            <div class="stat-icon">
              <md-icon>psychology</md-icon>
            </div>
            <div class="stat-content">
              <div class="stat-value">{{ formatNumber(stats.skill_mismatch) }}</div>
              <div class="stat-label">Skill Mismatch</div>
            </div>
          </div>

          <div class="stat-card biometric" @click="setFilter('all')">
            <div class="stat-icon">
              <md-icon>fingerprint</md-icon>
            </div>
            <div class="stat-content">
              <div class="stat-value">{{ formatNumber(stats.biometric_ready) }}</div>
              <div class="stat-label">Biometric Ready</div>
              <div class="stat-sub-label">{{ stats.biometric_partial }} partial</div>
            </div>
          </div>
        </div>

        <!-- Filters -->
        <div class="filters-bar md-layout md-gutter">
          <div class="md-layout-item md-size-20">
            <md-field>
              <label>Department</label>
              <md-select v-model="filters.department_id" @md-selected="onFilterChange">
                <md-option value="">All Departments</md-option>
                <md-option v-for="dept in departments" :key="dept.id" :value="dept.id">
                  {{ dept.name }}
                </md-option>
              </md-select>
            </md-field>
          </div>

          <div class="md-layout-item md-size-20">
            <md-field>
              <label>Port</label>
              <md-select v-model="filters.port_id" @md-selected="onFilterChange">
                <md-option value="">All Ports</md-option>
                <md-option v-for="port in ports" :key="port.id" :value="port.id">
                  {{ port.name }}
                </md-option>
              </md-select>
            </md-field>
          </div>

          <div class="md-layout-item md-size-20">
            <md-field>
              <label>Vendor</label>
              <md-select v-model="filters.vendor_id" @md-selected="onFilterChange">
                <md-option value="">All Vendors</md-option>
                <md-option v-for="vendor in vendors" :key="vendor.id" :value="vendor.id">
                  {{ vendor.name }}
                </md-option>
              </md-select>
            </md-field>
          </div>

          <div class="md-layout-item md-size-20">
            <md-field>
              <label>Per Page</label>
              <md-select v-model="pagination.perPage" @md-selected="onPerPageChange">
                <md-option v-for="item in perPageOptions" :key="item" :value="item">
                  {{ item }}
                </md-option>
              </md-select>
            </md-field>
          </div>

          <div class="md-layout-item md-size-20">
            <md-field>
              <label>Search</label>
              <md-input v-model="searchQuery" placeholder="Search by name, NIDA, phone..." @input="onSearchChange"/>
            </md-field>
          </div>
        </div>

        <!-- Loading -->
        <div v-if="isLoading" class="loading-container">
          <md-progress-spinner md-mode="indeterminate"></md-progress-spinner>
          <span>Loading labourers...</span>
        </div>

        <!-- Labourers Table -->
        <div v-else class="table-container">
          <md-table v-if="labourers && labourers.length">
            <md-table-row>
              <md-table-head>Labourer</md-table-head>
              <md-table-head>Current Workplace</md-table-head>
              <md-table-head>Skills Match</md-table-head>
              <md-table-head>Biometric Status</md-table-head>
              <md-table-head>Completeness</md-table-head>
              <md-table-head>Actions</md-table-head>
            </md-table-row>

            <md-table-row v-for="labourer in labourers" :key="labourer.id">
              <md-table-cell>
                <div class="labourer-info">
                  <div class="labourer-avatar">
                    <img v-if="labourer.image" :src="getImageUrl(labourer.image)" class="avatar"/>
                    <md-icon v-else class="avatar-placeholder">person</md-icon>
                  </div>
                  <div class="labourer-details">
                    <div class="labourer-name">{{ labourer.full_name }}</div>
                    <div class="labourer-meta">
                      <span class="labourer-number">{{ labourer.labourer_number }}</span>
                      <span class="labourer-phone">{{ labourer.phone_number }}</span>
                    </div>
                    <div class="labourer-nida">NIDA: {{ labourer.nida_number || 'N/A' }}</div>
                  </div>
                </div>
              </md-table-cell>

              <md-table-cell>
                <div v-if="labourer.active_workplace" class="workplace-info">
                  <div class="workplace-name">
                    {{ getWorkplaceName(labourer) }}
                  </div>
                  <div class="workplace-activity">
                    {{ getActivityName(labourer) }}
                  </div>
                  <div class="workplace-dates">
                    <span>Start: {{ formatDate(labourer.active_workplace.start_date) }}</span>
                    <span v-if="labourer.active_workplace.end_date"> | End: {{
                        formatDate(labourer.active_workplace.end_date)
                      }}</span>
                  </div>
                </div>
                <div v-else class="no-workplace">
                  <md-icon>business_off</md-icon>
                  <span>No active workplace</span>
                </div>
              </md-table-cell>

              <md-table-cell>
                <div v-if="labourer.has_active_workplace">
                  <div class="skill-match-progress">
                    <md-progress-spinner
                        v-if="labourer.skill_match_percentage < 100"
                        :value="labourer.skill_match_percentage"
                        md-mode="determinate"
                        :md-diameter="40"
                        :class="getSkillMatchClass(labourer.skill_match_percentage)">
                    </md-progress-spinner>
                    <md-icon v-else class="skill-matched-icon">check_circle</md-icon>
                    <div class="skill-match-percentage">{{ labourer.skill_match_percentage }}%</div>
                  </div>
                  <div class="skill-details"
                       v-if="labourer.skill_matching && labourer.skill_matching.missing_skills && labourer.skill_matching.missing_skills.length">
                    <span class="missing-label">Missing: {{ labourer.skill_matching.missing_skills.length }}</span>
                    <md-tooltip md-direction="top">
                      <div v-for="skill in labourer.skill_matching.missing_skills" :key="skill.id">
                        {{ skill.name }}
                      </div>
                    </md-tooltip>
                  </div>
                </div>
                <div v-else class="no-skill-data">N/A</div>
              </md-table-cell>

              <md-table-cell>
                <div class="biometric-status">
                  <div class="status-indicator" :class="getBiometricClass(labourer.biometric_status)">
                    <md-icon>{{ getBiometricIcon(labourer.biometric_status) }}</md-icon>
                  </div>
                  <div class="status-details">
                    <div class="status-label">{{ getBiometricLabel(labourer.biometric_status) }}</div>
                    <div class="status-issues"
                         v-if="labourer.biometric_status.issues && labourer.biometric_status.issues.length">
                      <span v-for="issue in labourer.biometric_status.issues.slice(0, 2)" :key="issue"
                            class="issue-badge">
                        {{ issue }}
                      </span>
                      <span v-if="labourer.biometric_status.issues.length > 2" class="issue-count">
                        +{{ labourer.biometric_status.issues.length - 2 }}
                      </span>
                    </div>
                  </div>
                </div>
              </md-table-cell>

              <md-table-cell>
                <div class="completeness-status">
                  <div class="completeness-badge" :class="{ complete: labourer.is_complete }">
                    <md-icon>{{ labourer.is_complete ? 'check_circle' : 'error' }}</md-icon>
                    <span>{{ labourer.is_complete ? 'Complete' : 'Incomplete' }}</span>
                  </div>
                  <div class="completion-rate">
                    Takeons: {{ labourer.completed_takeons }}/{{ labourer.total_takeons }}
                    ({{ labourer.completion_rate }}%)
                  </div>
                </div>
              </md-table-cell>

              <md-table-cell>
                <md-button class="md-icon-button md-primary" @click="viewLabourer(labourer.uuid)" title="View Details">
                  <md-icon>visibility</md-icon>
                </md-button>
                <md-button class="md-icon-button md-info" @click="editLabourer(labourer.uuid)" title="Edit">
                  <md-icon>edit</md-icon>
                </md-button>
                <md-button class="md-icon-button md-success" @click="syncBiotime(labourer)" title="Sync Biotime"
                           v-if="labourer.biometric_status && !labourer.biometric_status.is_ready">
                  <md-icon>sync</md-icon>
                </md-button>
              </md-table-cell>
            </md-table-row>
          </md-table>

          <div v-else class="empty-state">
            <md-icon>people_outline</md-icon>
            <p>No labourers found</p>
          </div>
        </div>

        <!-- Pagination -->
        <div class="pagination-footer">
          <div>
            <p class="card-category">
              Showing {{ from + 1 }} to {{ to }} of {{ totalItems }} entries
            </p>
          </div>
          <pagination
              class="pagination-no-border pagination-success"
              v-model="pagination.currentPage"
              :per-page="pagination.perPage"
              :total="totalItems"
              @update:model-value="onPageChange"
          />
        </div>
      </md-card-content>
    </md-card>
  </div>
</template>

<script>
import {mapActions} from 'vuex';
import Pagination from '@/components/Pagination';
import formMixin from '@/mixins/form-mixin';
import _ from 'lodash';

export default {
  name: 'LabourerReadinessDashboard',
  components: {
    Pagination
  },
  mixins: [formMixin],
  data: function () {
    return {
      labourers: [],
      stats: {
        total: 0,
        complete: 0,
        incomplete: 0,
        completion_percentage: 0,
        skill_matched: 0,
        skill_mismatch: 0,
        no_active_workplace: 0,
        biometric_ready: 0,
        biometric_partial: 0,
        average_skill_match_percentage: 0,
        average_completion_rate: 0
      },
      filters: {
        department_id: '',
        port_id: '',
        vendor_id: ''
      },
      searchQuery: '',
      filterStatus: 'all',
      pagination: {
        currentPage: 1,
        perPage: 20
      },
      perPageOptions: [10, 20, 50, 100],
      totalItems: 0,
      isLoading: false,
      departments: [],
      vendors: [],
      ports: [],
      hasRestoredState: false,
      stateKey: 'labourer_readiness_dashboard_state'
    };
  },
  computed: {
    from: function () {
      return this.pagination.perPage * (this.pagination.currentPage - 1);
    },
    to: function () {
      var highBound = this.from + this.pagination.perPage;
      if (this.totalItems < highBound) {
        highBound = this.totalItems;
      }
      return highBound;
    }
  },
  watch: {
    query: {
      handler: 'fetchData',
      immediate: true,
    },
    sortation: {
      handler: 'fetchData',
      immediate: false,
      deep: true,
    },
    pagination: {
      handler: 'fetchData',
      immediate: false,
      deep: true,
    },
  },
  created: function () {
    this.restoreState();
    this.fetchDepartments();
    this.fetchPorts();
    this.fetchVendors();
    this.fetchData();
  },
  beforeRouteLeave: function (to, from, next) {
    this.saveState();
    next();
  },
  methods: {
    ...mapActions('departments', {fetchDepartmentsAction: 'list'}),
    ...mapActions('ports', {fetchPortsAction: 'list'}),
    ...mapActions('vendors', {fetchVendorsAction: 'list'}),
    ...mapActions('labourers', {fetchReadinessAction: 'fetchReadiness'}),

    // ============ STATE PERSISTENCE ============
    saveState: function () {
      var state = {
        filters: {
          department_id: this.filters.department_id,
          port_id: this.filters.port_id,
          vendor_id: this.filters.vendor_id
        },
        searchQuery: this.searchQuery,
        filterStatus: this.filterStatus,
        pagination: {
          currentPage: this.pagination.currentPage,
          perPage: this.pagination.perPage
        },
        timestamp: new Date().getTime()
      };
      sessionStorage.setItem(this.stateKey, JSON.stringify(state));
    },

    restoreState: function () {
      var savedState = sessionStorage.getItem(this.stateKey);
      if (savedState) {
        try {
          var state = JSON.parse(savedState);
          // Check if less than 30 minutes old
          if (Date.now() - state.timestamp < 30 * 60 * 1000) {
            if (state.filters) {
              this.filters.department_id = state.filters.department_id || '';
              this.filters.port_id = state.filters.port_id || '';
              this.filters.vendor_id = state.filters.vendor_id || '';
            }
            this.searchQuery = state.searchQuery || '';
            this.filterStatus = state.filterStatus || 'all';
            if (state.pagination) {
              this.pagination.currentPage = state.pagination.currentPage || 1;
              this.pagination.perPage = state.pagination.perPage || 20;
            }
            this.hasRestoredState = true;
            return true;
          } else {
            sessionStorage.removeItem(this.stateKey);
          }
        } catch (e) {
          console.error('Error restoring state:', e);
        }
      }
      return false;
    },

    async fetchData() {
      this.isLoading = true;

      try {
        var params = {
          page: this.pagination.currentPage,
          per_page: this.pagination.perPage,
          status: this.filterStatus !== 'all' ? this.filterStatus : null,
          department_id: this.filters.department_id || null,
          port_id: this.filters.port_id || null,
          vendor_id: this.filters.vendor_id || null,
          search: this.searchQuery || null
        };

        await this.fetchReadinessAction(params);
        var response = this.$store.getters['labourers/list'];
        var meta = this.$store.getters['labourers/listMeta'];


        if (response) {
          this.labourers = response || [];
          if (meta) {
            this.totalItems = meta.total || 0;
            if (meta.stats) {
              this.stats = meta.stats;
            }
          }
        }
        // else if (response && response.list) {
        //   this.labourers = response.list || [];
        //   this.totalItems = response.meta ? response.meta.total : 0;
        //   if (response.meta && response.meta.stats) {
        //     this.stats = response.meta.stats;
        //   }
        // } else {
        //   this.labourers = response || [];
        //   this.totalItems = response ? response.length : 0;
        // }

        this.saveState();
      } catch (error) {
        console.error('Error fetching labourer readiness:', error);
        this.notifyError('Failed to load labourer data');
        this.notifyException(error);
      } finally {
        this.isLoading = false;
      }
    },

    getWorkplaceName: function (labourer) {
      if (labourer.active_workplace && labourer.active_workplace.workplace) {
        return labourer.active_workplace.workplace.name;
      }
      return 'N/A';
    },

    getActivityName: function (labourer) {
      if (labourer.active_workplace && labourer.active_workplace.activity) {
        return labourer.active_workplace.activity.name;
      }
      return 'N/A';
    },

    async fetchDepartments() {
      try {
        await this.fetchDepartmentsAction({page: {size: 1000}});
        this.departments = this.$store.getters['departments/list'] || [];
      } catch (error) {
        console.error('Error fetching departments:', error);
      }
    },

    async fetchPorts() {
      try {
        await this.fetchPortsAction({page: {size: 1000}});
        this.ports = this.$store.getters['ports/list'] || [];
      } catch (error) {
        console.error('Error fetching ports:', error);
      }
    },

    async fetchVendors() {
      try {
        await this.fetchVendorsAction({page: {size: 1000}});
        this.vendors = this.$store.getters['vendors/list'] || [];
      } catch (error) {
        console.error('Error fetching vendors:', error);
      }
    },

    onFilterChange: function () {
      this.pagination.currentPage = 1;
      this.fetchData();
    },

    setFilter: function (status) {
      this.filterStatus = status;
      this.pagination.currentPage = 1;
      this.fetchData();
    },

    onSearchChange: _.debounce(function () {
      this.pagination.currentPage = 1;
      this.fetchData();
    }, 500),

    onPerPageChange: function () {
      this.pagination.currentPage = 1;
      this.fetchData();
    },

    onPageChange: function (page) {
      this.pagination.currentPage = page;
      this.fetchData();
    },

    setStatusFilter: function (status) {
      this.filters.status = this.filters.status === status ? '' : status;
      this.pagination.currentPage = 1;
      this.fetchData();
    },

    getImageUrl: function (image) {
      if (!image) return null;
      if (image.startsWith('http')) return image;
      return process.env.VUE_APP_BASE_URL + image;
    },

    getSkillMatchClass: function (percentage) {
      if (percentage >= 80) return 'success';
      if (percentage >= 50) return 'warning';
      return 'danger';
    },

    getBiometricClass: function (status) {
      if (!status) return 'danger';
      if (status.is_ready) return 'success';
      if (status.is_partial) return 'warning';
      return 'danger';
    },

    getBiometricIcon: function (status) {
      if (!status) return 'error';
      if (status.is_ready) return 'fingerprint';
      if (status.is_partial) return 'warning';
      return 'error';
    },

    getBiometricLabel: function (status) {
      if (!status) return 'Not Configured';
      if (status.is_ready) return 'Ready';
      if (status.is_partial) return 'Partial';
      return 'Not Configured';
    },

    formatDate: function (date) {
      if (!date) return 'N/A';
      return new Date(date).toLocaleDateString();
    },

    formatNumber: function (value) {
      if (!value) return '0';
      return new Intl.NumberFormat().format(value);
    },

    viewLabourer: function (id) {
      this.saveState();
      this.$router.push({name: 'View Labourer', params: {id: id}});
    },

    editLabourer: function (id) {
      this.saveState();
      this.$router.push({name: 'Edit Labourer', params: {id: id}});
    },

    syncBiotime: async function (labourer) {
      try {
        console.log('Labourer:', labourer);
        labourer.id = labourer.uuid;
        await this.$store.dispatch('labourers/syncBiotime', labourer);
        this.notifySuccess('Syncing biometric data for ' + labourer.full_name);
      } catch (e) {
        this.notifyException(e)
      }

    }
  }
};
</script>

<style scoped>
.labourer-readiness-dashboard {
  padding: 20px;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
}

.stat-card {
  background: white;
  border-radius: 12px;
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.stat-card.active {
  border-color: #9c27b0;
  background: #f3e5f5;
}

.stat-card .stat-icon md-icon {
  font-size: 40px;
}

.stat-card.success .stat-icon md-icon {
  color: #4caf50;
}

.stat-card.warning .stat-icon md-icon {
  color: #ff9800;
}

.stat-card.info .stat-icon md-icon {
  color: #2196f3;
}

.stat-card.biometric .stat-icon md-icon {
  color: #9c27b0;
}

.stat-content {
  flex: 1;
}

.stat-value {
  font-size: 28px;
  font-weight: bold;
  color: #333;
}

.stat-label {
  font-size: 12px;
  color: #000;
}

.stat-percentage {
  font-size: 11px;
  color: #4caf50;
  margin-top: 4px;
}

.stat-sub-label {
  font-size: 10px;
  color: #000;
  margin-top: 2px;
}

.filters-bar {
  margin-bottom: 20px;
  padding: 16px;
  background: #f5f5f5;
  border-radius: 8px;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px;
  gap: 16px;
}

.table-container {
  overflow-x: auto;
  min-height: 400px;
}

.labourer-info {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 250px;
}

.labourer-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  overflow: hidden;
  background: #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.avatar {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-placeholder {
  font-size: 32px;
  color: #000;
}

.labourer-details {
  flex: 1;
}

.labourer-name {
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 4px;
}

.labourer-meta {
  display: flex;
  gap: 8px;
  font-size: 11px;
  color: #000;
  margin-bottom: 2px;
}

.labourer-nida {
  font-size: 10px;
  color: #000;
}

.workplace-info {
  min-width: 200px;
}

.workplace-name {
  font-weight: 500;
  font-size: 13px;
  margin-bottom: 2px;
}

.workplace-activity {
  font-size: 11px;
  color: #000;
  margin-bottom: 4px;
}

.workplace-dates {
  font-size: 10px;
  color: #000;
}

.no-workplace {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #ff9800;
  font-size: 12px;
}

.no-workplace md-icon {
  font-size: 18px;
}

.skill-match-progress {
  display: flex;
  align-items: center;
  gap: 8px;
}

.skill-match-percentage {
  font-size: 14px;
  font-weight: 600;
}

.skill-matched-icon {
  color: #4caf50;
  font-size: 40px;
}

.skill-details {
  margin-top: 4px;
}

.missing-label {
  font-size: 10px;
  color: #f44336;
  cursor: help;
}

.no-skill-data {
  color: #000;
  font-size: 12px;
}

.biometric-status {
  display: flex;
  align-items: center;
  gap: 12px;
}

.status-indicator {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.status-indicator.success {
  background: #e8f5e9;
  color: #4caf50;
}

.status-indicator.warning {
  background: #fff3e0;
  color: #ff9800;
}

.status-indicator.danger {
  background: #ffebee;
  color: #f44336;
}

.status-indicator md-icon {
  font-size: 24px;
}

.status-label {
  font-size: 13px;
  font-weight: 500;
}

.status-issues {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 4px;
}

.issue-badge {
  font-size: 9px;
  padding: 2px 6px;
  background: #ffebee;
  color: #c62828;
  border-radius: 10px;
}

.issue-count {
  font-size: 9px;
  padding: 2px 6px;
  background: #e0e0e0;
  color: #000;
  border-radius: 10px;
}

.completeness-status {
  text-align: center;
  min-width: 100px;
}

.completeness-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  background: #ffebee;
  color: #f44336;
  margin-bottom: 4px;
}

.completeness-badge.complete {
  background: #e8f5e9;
  color: #4caf50;
}

.completeness-badge md-icon {
  font-size: 16px;
}

.completion-rate {
  font-size: 10px;
  color: #000;
}

.empty-state {
  text-align: center;
  padding: 60px;
  color: #000;
}

.empty-state md-icon {
  font-size: 64px;
  margin-bottom: 16px;
}

.pagination-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #e0e0e0;
  flex-wrap: wrap;
  gap: 16px;
}

.pagination-info {
  font-size: 13px;
  color: #000;
}

/* Responsive */
@media (max-width: 768px) {
  .labourer-readiness-dashboard {
    padding: 10px;
  }

  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .filters-bar {
    flex-direction: column;
    gap: 10px;
  }

  .pagination-footer {
    flex-direction: column;
    align-items: center;
  }

  .labourer-info {
    min-width: 200px;
  }
}
</style>